import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { getOrganizationId } from "../utils/organizationUtils";
import {
  DEFAULT_EXPIRY_THRESHOLD_MONTHS,
  getMedicineExpiryStatus,
  isNearExpiryDate,
  monthsToThresholdDays,
  PHARMACY_EXPIRY_THRESHOLD_CHANGED,
  parseExpiryThresholdMonths,
} from "../utils/medicineExpiryThreshold";
import {
  getPharmacyExpiryThresholdMonths,
  savePharmacyExpiryThresholdMonths,
} from "../services/pharmacySettingsService";

export const usePharmacyExpiryThreshold = () => {
  const { user } = useAuth();
  const organizationId = getOrganizationId(user);
  const [months, setMonths] = useState(DEFAULT_EXPIRY_THRESHOLD_MONTHS);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const value = await getPharmacyExpiryThresholdMonths(organizationId);
      setMonths(value);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const onThresholdChanged = (event) => {
      const next = parseExpiryThresholdMonths(event?.detail?.months, null);
      if (next != null) {
        setMonths(next);
      } else {
        reload();
      }
    };
    window.addEventListener(PHARMACY_EXPIRY_THRESHOLD_CHANGED, onThresholdChanged);
    return () => {
      window.removeEventListener(PHARMACY_EXPIRY_THRESHOLD_CHANGED, onThresholdChanged);
    };
  }, [reload]);

  const saveThreshold = useCallback(
    async (newMonths) => {
      const saved = await savePharmacyExpiryThresholdMonths(organizationId, newMonths);
      setMonths(saved);
      return saved;
    },
    [organizationId]
  );

  const thresholdDays = useMemo(() => monthsToThresholdDays(months), [months]);

  const isNearExpiry = useCallback(
    (expiryDate) => isNearExpiryDate(expiryDate, months),
    [months]
  );

  const getExpiryStatus = useCallback(
    (expiryDate) => getMedicineExpiryStatus(expiryDate, months),
    [months]
  );

  return {
    months,
    thresholdDays,
    loading,
    reload,
    saveThreshold,
    isNearExpiry,
    getExpiryStatus,
  };
};
